import * as ExcelJS from 'exceljs';

import { Network } from '../../redux/networks';
import { NetworkBuyingOptions } from './streaming-buying-types';
import { getValidPlatforms } from './streaming-buying-utils';

const TEMPLATE_ROWS = 500;

const RED_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFF0000' }
};

export const TemplateType = {
  VIDEO: 'video',
  AUDIO: 'audio',
  DISPLAY: 'display'
} as const;
export type TemplateTypeValue = (typeof TemplateType)[keyof typeof TemplateType];

const PLATFORM_FILTER: Record<TemplateTypeValue, RegExp> = {
  video: /^Streaming/,
  audio: /^Audio/,
  display: /^Display/
};

const BASE_HEADERS = [
  'Order ID',
  'Network',
  'Platform',
  'Start Date',
  'End Date',
  'Description',
  'Content',
  'Additional',
  'Spend',
  'CPM',
  'DSP'
];

const AUDIO_HEADERS = [
  'Buy Type',
  'Show Title',
  'Spot Position',
  'Spot Rate',
  'Num of Spots',
  'Episode Air Dates'
];

const BASE_COLUMN_WIDTHS: Partial<ExcelJS.Column>[] = [
  { width: 12 }, // Order ID
  { width: 12 }, // Network
  { width: 22 }, // Platform
  { width: 12 }, // Start Date
  { width: 12 }, // End Date
  { width: 20 }, // Description
  { width: 15 }, // Content
  { width: 15 }, // Additional
  { width: 12 }, // Spend
  { width: 10 }, // CPM
  { width: 15 } // DSP
];

const AUDIO_COLUMN_WIDTHS: Partial<ExcelJS.Column>[] = [
  { width: 30 }, // Buy Type
  { width: 20 }, // Show Title
  { width: 18 }, // Spot Position
  { width: 12 }, // Spot Rate
  { width: 14 }, // Num of Spots
  { width: 20 } // Episode Air Dates
];

const AUDIO_BUY_TYPE_OPTIONS = [
  'Streaming Audio - DAI Impression',
  'Podcast - DAI Impression',
  'Podcast - DAI Episodic',
  'Podcast - Embedded'
];

const SPOT_POSITION_OPTIONS = [
  'Pre-Roll',
  'Mid-Roll',
  'Pre/Mid-Roll',
  'Post-Roll',
  'Pre/Mid/Post-Roll'
];

/**
 * Sanitize a network code for use as an Excel named range prefix.
 */
const toNamePrefix = (code: string): string => `N_${code.replace(/[^A-Za-z0-9_]/g, '_')}`;

/**
 * Writes a column of values to the mappings sheet and returns the Excel range string.
 */
const writeMappingColumn = (
  mappingsSheet: ExcelJS.Worksheet,
  col: number,
  header: string,
  values: string[]
): string | null => {
  // ExcelJS worksheets are mutated in place by design — there is no immutable cell API.
  /* eslint-disable no-param-reassign */
  mappingsSheet.getCell(1, col).value = header;
  values.forEach((val, i) => {
    mappingsSheet.getCell(i + 2, col).value = val;
  });
  /* eslint-enable no-param-reassign */
  if (values.length === 0) {
    return null;
  }
  const colLetter = mappingsSheet.getColumn(col).letter;
  return `Mappings!$${colLetter}$2:$${colLetter}$${values.length + 1}`;
};

/**
 * Adds a conditional formatting rule that turns cells red when the expression is true.
 */
const addRedConditionalFormat = (
  sheet: ExcelJS.Worksheet,
  ref: string,
  formula: string,
  priority: number
): void => {
  sheet.addConditionalFormatting({
    ref,
    rules: [
      {
        type: 'expression',
        formulae: [formula],
        priority,
        style: { fill: RED_FILL }
      }
    ]
  });
};

type NetworkOptionsMap = Record<string, NetworkBuyingOptions>;
type NetworkMap = Record<string, Network>;

interface OptionField {
  suffix: string;
  col: number;
}

/**
 * Filter platforms for a given network to only those matching the template type.
 */
const getFilteredPlatforms = (
  code: string,
  networkMap: NetworkMap,
  templateType: TemplateTypeValue
): string[] => {
  const allPlatforms = getValidPlatforms(code, networkMap);
  const filter = PLATFORM_FILTER[templateType];
  return allPlatforms.filter(p => filter.test(p));
};

/**
 * Builds and downloads a flight import template with data validation and conditional formatting.
 * Dropdowns for Platform, Content, and DSP are filtered by the selected Network.
 */
export const downloadFlightImportTemplate = async (
  networkOptions: NetworkOptionsMap,
  networkMap: NetworkMap,
  allNetworkCodes: string[],
  templateType: TemplateTypeValue
): Promise<void> => {
  const isAudio = templateType === 'audio';

  // Filter to only networks that have platforms for this template type
  const filteredNetworkCodes = allNetworkCodes.filter(code => {
    const platforms = getFilteredPlatforms(code, networkMap, templateType);
    return platforms.length > 0;
  });

  const headers = isAudio ? [...BASE_HEADERS, ...AUDIO_HEADERS] : BASE_HEADERS;
  const columnWidths = isAudio
    ? [...BASE_COLUMN_WIDTHS, ...AUDIO_COLUMN_WIDTHS]
    : BASE_COLUMN_WIDTHS;

  // Build option fields with correct column indices
  // Base: Platform=3, Content=7, DSP=11
  const optionFields: OptionField[] = [
    { suffix: 'Platform', col: 3 },
    { suffix: 'Content', col: 7 },
    { suffix: 'DSP', col: 11 }
  ];

  // Audio extra validated fields (after DSP col 11):
  // Buy Type=12, Spot Position=14
  const audioBuyTypeCol = 12;
  const audioSpotPositionCol = 14;

  const workbook = new ExcelJS.Workbook();

  // --- Template sheet (first tab) ---
  const templateSheet = workbook.addWorksheet('Template');

  // --- Mappings sheet (hidden, second tab) ---
  const mappingsSheet = workbook.addWorksheet('Mappings', { state: 'hidden' });

  // Col A: filtered network codes
  let colOffset = 2;
  writeMappingColumn(mappingsSheet, 1, 'Network', filteredNetworkCodes);

  // Per-network option columns + named ranges
  for (const code of filteredNetworkCodes) {
    const opts = networkOptions[code];
    const prefix = toNamePrefix(code);
    const optionValues: string[][] = [
      getFilteredPlatforms(code, networkMap, templateType),
      opts.content ?? [],
      opts.dsps ?? []
    ];

    for (let li = 0; li < optionFields.length; li++) {
      const values = optionValues[li];
      const { suffix } = optionFields[li];
      const col = colOffset + li;
      const range = writeMappingColumn(mappingsSheet, col, `${code}_${suffix}`, values);

      const namedRangeName = `${prefix}_${suffix}`;
      if (range) {
        workbook.definedNames.add(range, namedRangeName);
      } else {
        const colLetter = mappingsSheet.getColumn(col).letter;
        workbook.definedNames.add(`Mappings!$${colLetter}$1:$${colLetter}$1`, namedRangeName);
      }
    }
    colOffset += optionFields.length;
  }

  // Audio-specific mappings: Buy Type and Spot Position
  let buyTypeRange: string | null = null;
  let spotPositionRange: string | null = null;
  if (isAudio) {
    buyTypeRange = writeMappingColumn(mappingsSheet, colOffset, 'BuyType', AUDIO_BUY_TYPE_OPTIONS);
    colOffset++;
    spotPositionRange = writeMappingColumn(
      mappingsSheet,
      colOffset,
      'SpotPosition',
      SPOT_POSITION_OPTIONS
    );
  }

  // Template headers
  templateSheet.addRow(headers);
  templateSheet.getRow(1).font = { bold: true };
  templateSheet.columns = columnWidths;

  // Network dropdown (col B)
  const networkRange = `Mappings!$A$2:$A$${filteredNetworkCodes.length + 1}`;
  for (let row = 2; row <= TEMPLATE_ROWS; row++) {
    const excelRow = templateSheet.getRow(row);

    excelRow.getCell(2).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [networkRange],
      showErrorMessage: true,
      errorTitle: 'Invalid Network',
      error: 'Please select a valid network.'
    };

    // Dynamic dropdowns filtered by the selected network via INDIRECT
    for (const { suffix, col } of optionFields) {
      excelRow.getCell(col).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`INDIRECT("N_"&$B${row}&"_${suffix}")`],
        showErrorMessage: true,
        errorTitle: `Invalid ${suffix}`,
        error: `Please select a valid ${suffix.toLowerCase()} for the chosen network.`
      };
    }

    // Audio-specific dropdowns
    if (isAudio) {
      if (buyTypeRange) {
        excelRow.getCell(audioBuyTypeCol).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [buyTypeRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Buy Type',
          error: 'Please select a valid audio buy type.'
        };
      }
      if (spotPositionRange) {
        excelRow.getCell(audioSpotPositionCol).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [spotPositionRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Spot Position',
          error: 'Please select a valid spot position.'
        };
      }
    }
  }

  // --- Conditional formatting ---

  // Network (col B)
  addRedConditionalFormat(
    templateSheet,
    `B2:B${TEMPLATE_ROWS}`,
    `AND(B2<>"",COUNTIF(${networkRange},B2)=0)`,
    1
  );

  // Platform/Content/DSP - not valid for the selected network
  for (const { suffix, col } of optionFields) {
    const colLetter = String.fromCharCode(64 + col);
    addRedConditionalFormat(
      templateSheet,
      `${colLetter}2:${colLetter}${TEMPLATE_ROWS}`,
      `AND(${colLetter}2<>"",B2<>"",COUNTIF(INDIRECT("N_"&$B2&"_${suffix}"),${colLetter}2)=0)`,
      col
    );
  }

  // Start Date (col D)
  addRedConditionalFormat(
    templateSheet,
    `D2:D${TEMPLATE_ROWS}`,
    'AND(D2<>"",NOT(ISNUMBER(D2*1)))',
    20
  );
  // End Date (col E)
  addRedConditionalFormat(
    templateSheet,
    `E2:E${TEMPLATE_ROWS}`,
    'AND(E2<>"",OR(NOT(ISNUMBER(E2*1)),E2<D2))',
    21
  );
  // Spend (col I)
  addRedConditionalFormat(
    templateSheet,
    `I2:I${TEMPLATE_ROWS}`,
    'AND(I2<>"",OR(NOT(ISNUMBER(I2*1)),I2<0))',
    22
  );
  // CPM (col J)
  addRedConditionalFormat(
    templateSheet,
    `J2:J${TEMPLATE_ROWS}`,
    'AND(J2<>"",OR(NOT(ISNUMBER(J2*1)),J2<0))',
    23
  );

  // Audio-specific conditional formatting
  if (isAudio) {
    const buyTypeColLetter = String.fromCharCode(64 + audioBuyTypeCol);
    if (buyTypeRange) {
      addRedConditionalFormat(
        templateSheet,
        `${buyTypeColLetter}2:${buyTypeColLetter}${TEMPLATE_ROWS}`,
        `AND(${buyTypeColLetter}2<>"",COUNTIF(${buyTypeRange},${buyTypeColLetter}2)=0)`,
        30
      );
    }
    const spotPosColLetter = String.fromCharCode(64 + audioSpotPositionCol);
    if (spotPositionRange) {
      addRedConditionalFormat(
        templateSheet,
        `${spotPosColLetter}2:${spotPosColLetter}${TEMPLATE_ROWS}`,
        `AND(${spotPosColLetter}2<>"",COUNTIF(${spotPositionRange},${spotPosColLetter}2)=0)`,
        31
      );
    }
    // Spot Rate (col O=15) - non-negative number
    addRedConditionalFormat(
      templateSheet,
      `O2:O${TEMPLATE_ROWS}`,
      'AND(O2<>"",OR(NOT(ISNUMBER(O2*1)),O2<0))',
      32
    );
    // Num of Spots (col P=16) - non-negative number
    addRedConditionalFormat(
      templateSheet,
      `P2:P${TEMPLATE_ROWS}`,
      'AND(P2<>"",OR(NOT(ISNUMBER(P2*1)),P2<0))',
      33
    );
  }

  const typeLabel = templateType.charAt(0).toUpperCase() + templateType.slice(1);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${typeLabel}_Flight_Import_Template.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};
